import { ColumnDef } from '@tanstack/react-table'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Property } from '../../api/properties/types/response'
import { APPLICATION_SETTINGS, MODAL_TYPES } from '../../common/constants'
import { propertyResponseFields } from '../../common/response/property'
import { getPropertyQuickViewData } from '../../pages/Properties/utils'
import { showModal } from '../../store/modal'
import { formatNumberWithTwoDecimals } from '../../utils/general'
import { renderCurrency } from '../../utils/property'
import TableImage from '../TanstackTable/components/TableImage'

export const useTableColumns = (): ColumnDef<Property>[] => {
    const { t } = useTranslation()
    const dispatch = useDispatch()

    const currency = APPLICATION_SETTINGS.CURRENCY

    const handleQuickViewModal = useCallback(
        (data: Property) => {
            const filteredData = getPropertyQuickViewData(data)
            const payload = {
                modalType: MODAL_TYPES.QUICK_VIEW_MODAL,
                modalProps: {
                    data: filteredData,
                    appModule: 'property',
                },
            }
            dispatch(showModal(payload))
        },
        [dispatch]
    )

    return useMemo(
        (): ColumnDef<Property>[] => [
            {
                header: t('app.common.id'),
                id: propertyResponseFields.INTERNAL_APP_ID,
                accessorKey: propertyResponseFields.INTERNAL_APP_ID,
                enableSorting: false,
                size: 50,
            },
            {
                header: t('table.header.common.mainPhoto'),
                id: 'mainPhoto',
                enableSorting: false,
                size: 130,
                cell: ({ row }) => {
                    const photo = row.original.mainPhoto
                    if (!photo) return null
                    return <TableImage photoId={photo.id} updated={photo.updated} alt={photo.translations[photo.currentLocale]?.fileDescription} />
                },
            },
            {
                header: t('app.common.type'),
                id: propertyResponseFields.PROPERTY_TYPE,
                accessorFn: (row) => row.propertyType?.name,
                enableSorting: false,
                size: 120,
            },
            {
                header: t('app.common.city'),
                id: propertyResponseFields.CITY,
                accessorFn: (row) => row.city?.name,
                enableSorting: false,
                size: 130,
            },
            {
                header: t('app.common.widerArea'),
                id: propertyResponseFields.WIDER_AREA,
                accessorFn: (row) => row.widerArea?.name,
                enableSorting: false,
                size: 150,
            },
            {
                header: t('table.header.profile.price', { currency: renderCurrency(currency) }),
                id: propertyResponseFields.PRICE,
                enableSorting: false,
                size: 100,
                cell: ({ row }) => <>{row.original.price ? formatNumberWithTwoDecimals(row.original.price) : ''}</>,
            },
            {
                id: 'actions',
                enableResizing: false,
                enableSorting: false,
                size: 60,
                meta: { thDivClass: 'text-center', stopPropagation: true },
                cell: ({ row }) => (
                    <ul className="list-inline hstack gap-2 mb-0 justify-content-center">
                        <li className="list-inline-item">
                            <div
                                title={t('button.view')}
                                className="btn text-warning d-inline-block p-0"
                                onClick={() => handleQuickViewModal(row.original)}
                            >
                                <i className="mdi mdi-eye-outline fs-16"></i>
                            </div>
                        </li>
                    </ul>
                ),
            },
        ],
        [t, handleQuickViewModal, currency]
    )
}
